import dayjs from "dayjs";
import utc from "dayjs/plugin/utc"; // Import the utc plugin
import { monthNames } from "../../../../utils/common-utils";
dayjs.extend(utc);

const generateWeeklyDates = (startDate: string, endDate: string): string[] => {
  const weeklyDates: string[] = [];
  let start = dayjs(startDate).utc().startOf("day");
  const end = dayjs(endDate).utc().startOf("day");

  while (start.isBefore(end) || start.isSame(end)) {
    weeklyDates.push(start.format("YYYY-MM-DD"));
    start = start.add(7, "day");
  }

  return weeklyDates;
};

export const parseBlocksOff = (
  blocks: Record<string, any>,
  dateString: string
) => {
  const targetDate = new Date(dateString);
  const year = targetDate.getFullYear();
  const month = targetDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const parsedList: { label: string; list: any[] }[] = [];

  const updatedBlockList = Object.entries(blocks).map(([key, block]) => {
    const startDate = new Date(block.start_date);
    const endDate = block.end_date ? new Date(block.end_date) : null;

    if (block.repeat_interval === "Weekly") {
      const weekList = generateWeeklyDates(
        startDate.toISOString(),
        endDate?.toISOString() || new Date().toISOString()
      );
      return { ...block, weekList };
    }

    return block;
  });

  for (let day = 1; day <= daysInMonth; day++) {
    const currentDate = new Date(year, month, day);
    const currentDateStr = currentDate.toLocaleDateString("en-CA");

    const label = currentDate
      .toLocaleDateString("en-US", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      })
      .replace(",", "");

    const blockList = Object.values(updatedBlockList).filter((block, index) => {
      const startDate = new Date(block.start_date);
      const endDate: any = block.end_date ? new Date(block.end_date) : null;
      const repeatExceptions = block.repeat_exceptions || [];
      const repeatInterval = block.repeat_interval || null;

      const isException = repeatExceptions.some((exception: any) => {
        const normalizedException = new Date(exception);
        return (
          normalizedException.toLocaleDateString("en-CA") === currentDateStr
        );
      });

      if (isException) return false;

      const startDateStr = startDate.toLocaleDateString("en-CA");
      const endDateStr: any = endDate
        ? endDate.toLocaleDateString("en-CA")
        : null;

      // If there's no endDate, return only on start_date
      if (!endDate) return currentDateStr === startDateStr;

      if (repeatInterval) {
        switch (repeatInterval) {
          case "Weekly": {
            if (block.weekList.includes(currentDateStr)) return true;
            return false;
          }
          case "Daily": {
            if (
              !endDate ||
              (currentDateStr >= startDateStr && currentDateStr <= endDateStr)
            )
              return true;
            return false;
          }
          default:
            return false;
        }
      }
      if (endDate)
        return currentDateStr >= startDateStr && currentDateStr <= endDateStr;
      else return currentDateStr === startDateStr;
    });

    parsedList.push({
      label,
      list: Array.from(new Set(blockList.map((x) => x.id)))
        .map((id) => blockList.find((x) => x.id === id))
        ?.map((x) => ({ ...x, label })),
    });
  }

  const filteredParsedList = parsedList.filter((item) => item.list.length > 0);
  return filteredParsedList.length > 0 ? filteredParsedList : null;
};

export const calculateBlockoffDays = (item: any) => {
  const startDate = new Date(item.start_date);
  const endDate = new Date(item.end_date);

  const diffTime = endDate.getTime() - startDate.getTime();
  const diffDays = diffTime / (1000 * 3600 * 24);

  return diffDays + 1; // Add 1 because the blockoff includes both the start and end days
};

export const convertTo24HourFormat = (timeString: string): string => {
  const [time, period] = timeString.split(" ");
  let [hours, minutes] = time.split(":").map(Number);
  if (period === "PM" && hours !== 12) hours += 12;
  else if (period === "AM" && hours === 12) hours = 0;
  return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
};

export const convertTo12HourFormat = (time: string) => {
  const [hours, minutes] = time.split(":");
  let hour = parseInt(hours);
  const period = hour >= 12 ? "PM" : "AM";

  if (hour > 12) hour = hour - 12;
  else if (hour === 0) hour = 12;

  const formattedHour = hour < 10 ? `0${hour}` : hour;
  return `${formattedHour}:${minutes} ${period}`;
};

export const RepeatingIntervals = [
  { label: "Does not repeat", value: "" },
  { label: "Daily", value: "Daily" },
  { label: "Weekly", value: "Weekly" },
];

export const parsedBlocksOffList = (data: any, dateString: string) => {
  const targetDate = new Date(dateString);
  const year = targetDate.getFullYear();
  const month = targetDate.getMonth();

  const {
    blockoffs: { single = [], parser = [] },
  } = data;

  const flatList = [...single, ...parser];
  const blockoffsByDate: Record<string, any[]> = {};

  flatList.forEach((block) => {
    const blockStartDate = new Date(block.start);

    const blockYear = blockStartDate.getFullYear();
    const blockMonth = blockStartDate.getMonth();
    const formattedDate = blockStartDate.toISOString().split("T")[0];

    if (blockYear === year && blockMonth === month) {
      if (!blockoffsByDate[formattedDate]) blockoffsByDate[formattedDate] = [];
      blockoffsByDate[formattedDate].push(block);
    }
  });

  const parsedData = Object.keys(blockoffsByDate)
    .map((date) => {
      const [year, month, day] = date.split("-");
      const monthIndex = parseInt(month, 10) - 1;
      const monthName = monthNames[monthIndex];
      const label = `${monthName} ${parseInt(day, 10)}, ${year}`;
      return {
        label,
        list: blockoffsByDate[date].sort(
          (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
        ),
      };
    })
    .sort((a, b) => new Date(a.label).getTime() - new Date(b.label).getTime());
  return parsedData;
};
